// /login/verify ページ — 6 桁コード入力

import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { ApiError } from "../api/client";

interface LocationState {
  challenge_id: string;
  masked_destination: string;
  expires_in_seconds: number;
}

export function LoginVerify() {
  const { verifyLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // challenge_id が無ければ /login に戻す
    if (!state?.challenge_id) {
      navigate("/login", { replace: true });
    }
  }, [state, navigate]);

  if (!state?.challenge_id) {
    return null;
  }

  function handleInput(index: number, value: string) {
    // 数字 1 文字だけ受け付ける
    const v = value.replace(/\D/g, "").slice(0, 1);
    const next = [...code];
    next[index] = v;
    setCode(next);
    if (v && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    // useEffect 内の redirect が走る前に submit されるレースを排除
    if (!state) return;
    const full = code.join("");
    if (full.length !== 6) {
      setError("6 桁の数字を入力してください");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const resp = await verifyLogin(state.challenge_id, full);
      if (resp.status === "authenticated") {
        navigate("/dashboard", { replace: true });
      }
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "検証に失敗しました";
      setError(msg);
      setCode(["", "", "", "", "", ""]);
      inputsRef.current[0]?.focus();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <h1 className="login-title">メール認証コード</h1>
        <p className="login-subtitle">
          <strong>{state.masked_destination}</strong> に送信された 6 桁コードを入力してください
        </p>

        <form className="login-form" onSubmit={onSubmit}>
          <div className="login-verify-code">
            {code.map((c, i) => (
              <input
                key={i}
                ref={(el) => {
                  inputsRef.current[i] = el;
                }}
                className="login-verify-digit"
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={c}
                onChange={(e) => handleInput(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                autoFocus={i === 0}
                disabled={submitting}
              />
            ))}
          </div>

          {error && <div className="login-error">{error}</div>}

          <button
            className="login-submit"
            type="submit"
            disabled={submitting || code.some((c) => !c)}
          >
            {submitting ? "検証中..." : "認証する"}
          </button>

          <div className="login-divider" />
          <button
            type="button"
            className="login-link-button"
            onClick={() => navigate("/login", { replace: true })}
          >
            ログイン画面に戻る
          </button>

          <p className="login-demo-hint">
            <strong>デモ:</strong> バックエンドの Flask コンソールに 6 桁コードが出力されています。<br />
            <code>TRIGGER_CHALLENGE=false</code> で起動すると検証はスキップされます。
          </p>
        </form>
      </div>
    </div>
  );
}